import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, CircularProgress, Grid } from 'src/components/UI'
import { estimatesByCategoryActions } from 'src/ducks/actions'
import { getEstimatesByStage } from 'src/ducks/selectors'
import { EstimateItem } from 'src/ducks/types'
import ColumnCard from '../ColumnCard'
import ColumnHeader from '../ColumnHeader'
import { ColumnLaneProps } from './types'

import { debounce } from 'lodash'
import { getEstimatesFilter } from 'src/ducks/filters/selector'
import { SearchParams } from 'src/ducks/searches/types'
import useStyles from './styles'


const SECTION_COUNT = 20

const ColumnLane: FC<ColumnLaneProps> = ({ stage, searchParams, headerBackground, laneBackground }) => {
  const dispatch = useDispatch()

  const styles = useStyles()

  const estimates = useSelector(getEstimatesByStage(stage))
  const estimatesFilters = useSelector(getEstimatesFilter())

  const estimatesBoxRef = useRef<HTMLDivElement>(null);
  const prevRefreshTriggerRef = useRef<number | undefined>(undefined);
  const skipCacheRef = useRef<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false)
  const [initialEstimateCount, setInitialEstimateCount] = useState(SECTION_COUNT)

  // Save only the filters that affect the search, including refreshTrigger
  const searchFilters = useMemo(() => {
    const { search, assignedOption, dueDateOption, estimateNumber, estimator, inUseOption, selectedAffiliates, territory, dueDateRange, refreshTrigger } = estimatesFilters
    return {
      search,
      assignedOption,
      dueDateOption,
      estimateNumber,
      estimator,
      inUseOption,
      selectedAffiliates,
      territory,
      dueDateRange,
      refreshTrigger
    }
  }, [estimatesFilters])

  const { search, assignedOption, dueDateOption, estimateNumber, estimator, inUseOption, selectedAffiliates, territory, dueDateRange, refreshTrigger } = searchFilters

  const handleScroll = () => {
    const element = estimatesBoxRef.current;
    if (element) {
      const { scrollTop, scrollHeight, clientHeight } = element;
      if (scrollTop + clientHeight >= scrollHeight - 2) {
        setInitialEstimateCount((count) => count + SECTION_COUNT);
      }
    }
  };

  const getParams = () => {
    const searchParams = {} as Partial<SearchParams>

    if (search) { searchParams.search = search }

    if (estimateNumber) { searchParams.publicId = estimateNumber }

    if (territory && territory?.label !== 'All Territories') { searchParams.territory = territory.key }

    if (estimator && estimator.label !== 'All Estimators') { searchParams.estimatorId = estimator.id }

    const resAffiliates = selectedAffiliates?.filter(aff => !aff.hide).reduce((acc: any, curr: any) => acc.concat(curr.key), [])
    const queryAffiliates = resAffiliates?.join(',')
    if (resAffiliates?.length !== 0 && resAffiliates && !resAffiliates?.includes('all')) { searchParams.affiliate = queryAffiliates }

    if (inUseOption?.length && inUseOption !== 'all') searchParams.currentlyInUse = inUseOption

    if (assignedOption?.length && assignedOption !== 'all') searchParams.hasEstimatorId = assignedOption

    if (dueDateOption?.length && dueDateOption !== 'all') searchParams.dueDate = `${Math.floor(new Date().getTime() / 1000)}`

    if (dueDateRange?.startDate && dueDateRange?.endDate) {
      const startDate = dueDateRange.startDate instanceof Date ? dueDateRange.startDate : new Date(dueDateRange.startDate)
      const endDate = dueDateRange.endDate instanceof Date ? dueDateRange.endDate : new Date(dueDateRange.endDate)

      startDate.setHours(0, 0, 0, 0)

      endDate.setHours(23, 59, 59, 999)

      searchParams.dueDateStart = Math.floor(startDate.getTime() / 1000)
      searchParams.dueDateEnd = Math.floor(endDate.getTime() / 1000)
    }

    return searchParams
  }

  const clearState = () => {
    dispatch(
      estimatesByCategoryActions.resetEstimatesByStage({ stage })
    )
  }

  const fetchEstimatesByStage = (params: Partial<SearchParams>, count: number) => {
    // Read the ref to check if we should skip the cache
    const shouldSkipCache = skipCacheRef.current

    // Reset the ref after reading it
    skipCacheRef.current = false

    dispatch(
      estimatesByCategoryActions.fetchEstimatesByStage(
        {
          stage,
          start: 0,
          limit: count,
          searchParams: {
            ...searchParams,
            ...params,
            ...(shouldSkipCache ? { cache: false } : {})
          }
        },
        (succ: boolean, limit?: number) => {
          setLoading(false)
          setInitialEstimateCount(limit || count)
        }
      )
    )
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchEstimatesByStage = useMemo(() => 
    debounce((params: Partial<SearchParams>, count: number) => {
      fetchEstimatesByStage(params, count);
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (loading || initialEstimateCount === SECTION_COUNT || (estimates?.items.length || 0) >= (Number(estimates?.total || 0))) return;

    setLoading(true)

    // In infinite scroll, never use cache: false (skipCacheRef is already false)
    debouncedFetchEstimatesByStage(getParams(), initialEstimateCount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEstimateCount])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    clearState()

    setInitialEstimateCount(SECTION_COUNT)
    setLoading(true)

    // Detect if refreshTrigger changed (indicates an assignment)
    const refreshTriggerChanged = prevRefreshTriggerRef.current !== undefined &&
      prevRefreshTriggerRef.current !== refreshTrigger

    // Update the previous value
    prevRefreshTriggerRef.current = refreshTrigger

    // If refreshTrigger changed, set the flag to use cache: false
    if (refreshTriggerChanged) {
      skipCacheRef.current = true
      // Call directly without debounce for immediate execution
      fetchEstimatesByStage(getParams(), initialEstimateCount)
    } else {
      // skipCacheRef is already false, use debounce normally
      debouncedFetchEstimatesByStage(getParams(), initialEstimateCount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    estimatesBoxRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      clearState()
      // eslint-disable-next-line react-hooks/exhaustive-deps
      estimatesBoxRef.current?.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box className={styles.container} bgcolor={laneBackground || '#FAFDFF'}>
      <ColumnHeader
        count={Number(estimates?.total || 0)}
        status={stage}
        backgroundColor={headerBackground || "#E6F7FF"}
        price={Number(estimates?.totalValue || 0) || undefined}
        dueToday={Number(estimates?.dueToday || 0) || undefined}
      />
      <Box ref={estimatesBoxRef} className={styles.column}>
        {estimates?.items
          ?.slice(0, initialEstimateCount)
          ?.map((estimate: EstimateItem) => (
            <ColumnCard estimate={estimate} key={estimate.id} />
          ))}

        {loading && (
          <Grid container justifyContent='center'>
            <CircularProgress />
          </Grid>
        )}
      </Box>
    </Box>
  )
}

export default ColumnLane
